/**
 * Reiter "Lieferverzug" — von glawel-liefertermin und sporttore-liefertermin
 * gemeinsam genutzt. Liest genau einen Endpunkt:
 *
 *   GET /programmes/<programmeId>/analytics
 *
 * Kein eigener Job, kein Schreibzugriff: die Zahlen entstehen beim Import und
 * werden hier nur gelesen (kein Schedule).
 *
 * ANSICHT (Wunsch Matze 18.08.2026)
 * ------------------------------------------------------------------
 * Eine Steuerleiste schaltet ALLE Verlaufs-Karten gemeinsam zwischen
 * KW / Monat / Jahr um; der Zeitraum ist frei waehlbar — ab dem ersten
 * auswertbaren Import. Aggregiert wird im Frontend aus den Roh-Faellen
 * (`daten.faelle`), die Rechenregeln liegen in viz.ts (pur, getestet).
 *
 * AUFBAU
 * ------------------------------------------------------------------
 * 1. Leitzahl + Erklaerkasten
 * 2. Kacheln (Bestand — unabhaengig vom Zeitraum)
 * 3. Steuerleiste: KW/Monat/Jahr + Zeitraum
 * 4. Haelt der Lieferant seine gemeldeten Termine?
 * 5. Das 6-Wochen-Ziel (AB → Versand ueber zielVorlaufTage = Warn-Schwelle
 *    des Imports) — "ich muss wissen wieviel die 6 Wochen gerissen haben"
 * 6. Prognose (Zukunft, immer KW)
 * 7. Umplanungen, 8. Wartezeit, 9. Auffaellige Auftraege, 10. Artikel/Hersteller
 */

import { useCallback, useEffect, useMemo, useState, type CSSProperties, type ReactNode } from 'react';
import { api } from '@libero/core/services/api';
import { AlertTriangle, RefreshCw } from 'lucide-react';
import {
  FazitBox, Karte, Kachel, KachelReihe, Legende, Leitzahl, Tabelle, Zelle, QuoteZelle, DuennMarke,
  KLASSEN_LEGENDE, type LegendenEintrag,
} from './bausteine';
import {
  PrognoseDiagramm, TreueDiagramm, VerschiebungenDiagramm, VorlaufDiagramm, ZielDiagramm,
} from './diagramme';
import {
  FARBEN, GANZER_ZEITRAUM, baueTreueZeilen, bauePrognoseZeilen, baueVerschiebungZeilen,
  baueVorlaufZeilen, baueZielZeilen, datumKurz, fazitGruppen, fazitPrognose, fazitTreue,
  fazitVerschiebungen, fazitVorlauf, fazitZiel, kwLang, offeneUeberZiel, periodeLabel,
  periodeLang, prozent, sortiereGruppen, tageMitWochen, teileGruppen, verfuegbarePerioden,
  verschiebungAchse, zahl, zielRisse,
  type Granularitaet, type Zeitraum,
} from './viz';
import type { TreueZeile } from './viz';
import type { AnalyticsErgebnis, AuffaelligerAuftrag, GruppenQuote } from './typen';

const QUOTEN_LEGENDE: LegendenEintrag[] = [
  { label: 'letzte Meldung gehalten', farbe: FARBEN.serie1, form: 'linie' },
  { label: 'schon die erste Meldung gehalten', farbe: FARBEN.serie2, form: 'linieGestrichelt' },
];

const PROGNOSE_LEGENDE: LegendenEintrag[] = [
  { label: 'vom Lieferanten angekündigt', farbe: FARBEN.serie1Hell },
  { label: 'realistisch zu erwarten', farbe: FARBEN.serie1Dunkel, form: 'linie' },
];

const VERSCHIEBUNG_LEGENDE: LegendenEintrag[] = [
  { label: 'nach hinten verschoben', farbe: FARBEN.kritisch },
  { label: 'nach vorne gezogen', farbe: FARBEN.gut },
];

/**
 * `ladeDaten` ueberschreibt die Datenquelle. Die interne Ansicht laesst sie
 * weg und liest den auth-geschuetzten Endpunkt; die login-lose externe
 * Ansicht (glawel PublicApp) gibt hier ihren eigenen Token-Abruf mit — so
 * rechnet beides mit derselben Panel-Logik. Wirft die Funktion einen Fehler
 * mit `klartext`, wird dieser Text dem Leser direkt gezeigt.
 */
export default function LieferverzugPanel({
  programmeId,
  ladeDaten,
  variante = 'intern',
}: {
  programmeId: string;
  ladeDaten?: () => Promise<AnalyticsErgebnis>;
  /**
   * 'extern' = Ansicht fuer den Lieferanten selbst. Gleiche Zahlen wie intern
   * (die ganze Liste ist ja seine), nur ohne die Marken-Karte: die zeigt
   * Plenty-Stammdaten und liest sich fuer ihn wie eine Lieferantenaufteilung,
   * die es nicht gibt (gemessen 03.09.2026: 0 Auftraege stehen in beiden
   * Lieferantenlisten).
   */
  variante?: 'intern' | 'extern';
}) {
  const [daten, setDaten] = useState<AnalyticsErgebnis | null>(null);
  const [laedt, setLaedt] = useState(true);
  const [fehler, setFehler] = useState<string | null>(null);
  const [gran, setGran] = useState<Granularitaet>('kw');
  const [zeitraum, setZeitraum] = useState<Zeitraum>(GANZER_ZEITRAUM);

  const laden = useCallback(async () => {
    setLaedt(true);
    setFehler(null);
    try {
      if (ladeDaten) {
        setDaten(await ladeDaten());
      } else {
        const resp = await (api as any).get(`/programmes/${programmeId}/analytics`);
        if (resp.data?.success) setDaten(resp.data.data);
        else setFehler('Die Auswertung konnte nicht geladen werden.');
      }
    } catch (e: any) {
      setFehler(
        e?.response?.data?.error?.message
        ?? e?.klartext
        ?? 'Die Auswertung konnte nicht geladen werden.',
      );
    } finally {
      setLaedt(false);
    }
  }, [programmeId, ladeDaten]);

  useEffect(() => { void laden(); }, [laden]);

  const faelle = daten?.faelle ?? [];
  const toleranz = daten?.toleranzWochen ?? 1;
  const zielTage = daten?.zielVorlaufTage ?? 42;
  const zielWochen = Math.round(zielTage / 7);

  const perioden = useMemo(
    () => (daten ? verfuegbarePerioden(faelle, daten.verschiebungen, gran) : []),
    [daten, faelle, gran],
  );
  const treueZeilen = useMemo(() => baueTreueZeilen(faelle, gran, zeitraum, toleranz), [faelle, gran, zeitraum, toleranz]);
  const zielZeilen = useMemo(() => baueZielZeilen(faelle, gran, zeitraum, zielTage), [faelle, gran, zeitraum, zielTage]);
  const risse = useMemo(() => zielRisse(faelle, gran, zeitraum, zielTage), [faelle, gran, zeitraum, zielTage]);
  const offeneRisse = useMemo(
    () => (daten ? offeneUeberZiel(faelle, daten.stand, zielTage) : []),
    [daten, faelle, zielTage],
  );
  const verschiebungZeilen = useMemo(
    () => (daten ? baueVerschiebungZeilen(daten.verschiebungen, gran, zeitraum) : []),
    [daten, gran, zeitraum],
  );
  const vorlaufZeilen = useMemo(() => baueVorlaufZeilen(faelle, gran, zeitraum), [faelle, gran, zeitraum]);
  const prognoseZeilen = useMemo(() => (daten ? bauePrognoseZeilen(daten.prognose) : []), [daten]);

  if (laedt && !daten) {
    return <Platzhalter text="Auswertung wird berechnet…" />;
  }
  if (fehler && !daten) {
    return (
      <div style={{
        padding: '1rem', border: '1px solid #f5c6cb', background: '#fdf0f1',
        borderRadius: '8px', color: '#a02020', display: 'flex', gap: '0.5rem', alignItems: 'center',
      }}>
        <AlertTriangle size={16} />
        <span>{fehler}</span>
        <button onClick={() => void laden()} style={knopfStil}>Erneut versuchen</button>
      </div>
    );
  }
  if (!daten) return null;

  const k = daten.kopf;
  const anteilRekonstruiert = daten.datenbasis.snapshots > 0
    ? daten.datenbasis.rekonstruiert / daten.datenbasis.snapshots
    : 0;

  return (
    // Bei einem Neuladen bleibt die alte Ansicht stehen (leicht abgeblendet) —
    // kein Skelett-Blitz, kein Sprung im Layout.
    <div style={{ opacity: laedt ? 0.6 : 1, transition: 'opacity 120ms linear' }}>
      {/* Kopf: Stand der Daten + Aktualisieren */}
      <div style={{
        display: 'flex', justifyContent: 'space-between', alignItems: 'flex-end',
        gap: '1rem', marginBottom: '1rem', flexWrap: 'wrap',
      }}>
        <p style={{ margin: 0, fontSize: '0.8125rem', color: FARBEN.tinteMittel, lineHeight: 1.6 }}>
          Stand {datumKurz(daten.stand)} · aktuelle Woche KW {daten.aktuelleWoche.kw}/{daten.aktuelleWoche.jahr} ·
          {' '}letzter Import {datumKurz(daten.datenbasis.letzterLauf)} ·
          {' '}{zahl(daten.datenbasis.auftraege)} Aufträge aus {zahl(daten.datenbasis.snapshots)} Schnappschüssen
          {' '}seit {datumKurz(daten.datenbasis.ersterLauf)}
        </p>
        <button onClick={() => void laden()} disabled={laedt} style={knopfStil}>
          <RefreshCw size={13} />
          Aktualisieren
        </button>
      </div>

      {/* Leitzahl + Einordnung */}
      <div style={{ display: 'flex', gap: '0.75rem', flexWrap: 'wrap', marginBottom: '1.25rem' }}>
        <Leitzahl
          quote={k.quotePuenktlich8Wochen}
          unterzeile="Anteil der Aufträge, die in der zuletzt gemeldeten Liefer-KW ausgeliefert wurden (eine Woche Messtoleranz eingerechnet)"
        />
        <div style={{
          background: FARBEN.flaeche, border: `1px solid ${FARBEN.linie}`, borderRadius: '10px',
          padding: '1.25rem 1.5rem', flex: '1 1 300px', fontSize: '0.8125rem',
          color: FARBEN.tinteMittel, lineHeight: 1.65,
        }}>
          <div style={{ fontWeight: 650, color: FARBEN.tinteStark, marginBottom: '0.4rem' }}>
            So entstehen diese Zahlen
          </div>
          <p style={{ margin: '0 0 0.4rem' }}>
            Jede Woche kommt die Liste des Lieferanten mit den gemeldeten Liefer-KWs. Wir merken uns
            jeden Stand und vergleichen ihn mit dem tatsächlichen <strong>Warenausgang</strong>. Weil die Liste
            nur wöchentlich kommt, kann eine pünktliche Lieferung eine Woche „zu spät" aussehen — deshalb
            rechnet alles hier mit einer Woche Toleranz.
          </p>
          <p style={{ margin: 0 }}>
            <strong>Letzte Meldung</strong> = die zuletzt genannte Liefer-KW (hält der Lieferant, was er zuletzt sagt?). {' '}
            <strong>Erste Meldung</strong> = seine allererste Terminangabe (wie gut ist seine Planung von Anfang an?).
          </p>
        </div>
      </div>

      {/* Bestand */}
      <KachelReihe>
        <Kachel label="offen" wert={zahl(k.offen)} zusatz="noch nicht ausgeliefert" />
        <Kachel label="Warenausgang gesamt" wert={zahl(k.geliefertGesamt)} zusatz="seit Beginn der Messung" />
        <Kachel
          label="still verzögert" wert={zahl(k.stillVerzoegert)} ton={k.stillVerzoegert > 0 ? 'warnung' : 'neutral'}
          zusatz="Termin verstrichen, keine neue KW"
          erklaerung="Die zuletzt gemeldete Liefer-KW ist komplett vorbei, es gibt weder Warenausgang noch Storno — und der Lieferant hat KEINEN neuen Termin gemeldet. Hier lohnt das Nachhaken."
        />
        <Kachel
          label="verschwunden" wert={zahl(k.verschwunden)} ton={k.verschwunden > 0 ? 'kritisch' : 'neutral'}
          zusatz="aus der Liste gefallen"
        />
        <Kachel label="ohne Termin" wert={zahl(k.ohneTermin)} zusatz="unverplant in der Liste" />
        <Kachel
          label="mehrfach verschoben" wert={zahl(k.mehrfachVerschoben)} ton={k.mehrfachVerschoben > 0 ? 'warnung' : 'neutral'}
          zusatz="3 Umplanungen oder mehr"
        />
        <Kachel
          label={`über ${zielWochen}-Wochen-Ziel`} wert={zahl(offeneRisse.length)}
          ton={offeneRisse.length > 0 ? 'kritisch' : 'gut'}
          zusatz="offen und AB älter als das Ziel"
        />
        <Kachel
          label="typische Verspätung"
          wert={k.medianVerspaetungWochen === null ? '—' : `${zahl(k.medianVerspaetungWochen)} Wo.`}
          zusatz="wenn ein Termin rutscht"
        />
        <Kachel
          label="typische Wartezeit"
          wert={k.medianVorlaufTage === null ? '—' : `${zahl(k.medianVorlaufTage)} T.`}
          zusatz={k.medianVorlaufTage === null
            ? 'Auftragsbestätigung bis Versand'
            : `= ${(k.medianVorlaufTage / 7).toLocaleString('de-DE', { maximumFractionDigits: 1 })} Wochen von AB bis Versand`}
        />
        <Kachel
          label="AB-Datum fehlerhaft" wert={zahl(k.abDatumFehlerhaft)} ton={k.abDatumFehlerhaft > 0 ? 'warnung' : 'neutral'}
          zusatz="AB datiert nach Lieferung"
        />
      </KachelReihe>

      {/* Steuerleiste: Ansicht + Zeitraum — wirkt auf alle Verlaufs-Karten. */}
      <Steuerleiste
        gran={gran}
        aufGran={(g) => { setGran(g); setZeitraum(GANZER_ZEITRAUM); }}
        zeitraum={zeitraum}
        aufZeitraum={setZeitraum}
        perioden={perioden}
      />

      {/* 4. Termintreue der Lieferantenmeldungen */}
      <Karte
        titel="Hält der Lieferant seine gemeldeten Termine?"
        frage="Oben: ausgelieferte Aufträge — grün kam wie zuletzt gemeldet, gelb/orange/rot kam später. Unten: derselbe Verlauf als Anteil."
        hinweis="Hohle Punkte mit Fallzahl bedeuten: weniger als 10 Aufträge — der Prozentwert kann dann durch einen einzigen Auftrag springen, nur als Tendenz lesen."
        tabelle={<TreueTabelle zeilen={treueZeilen} gran={gran} />}
      >
        {treueZeilen.length === 0 ? <Leer /> : (
          <>
            <Legende eintraege={KLASSEN_LEGENDE} />
            <TreueDiagramm zeilen={treueZeilen} gran={gran} importWochen={daten.importWochen ?? []} />
            <div style={{ marginTop: '0.5rem' }}>
              <Legende eintraege={QUOTEN_LEGENDE} />
            </div>
            <FazitBox text={fazitTreue(treueZeilen, toleranz)} />
          </>
        )}
      </Karte>

      {/* 5. Das 6-Wochen-Ziel */}
      <ZielKarte
        zeilen={zielZeilen}
        risse={risse}
        offene={offeneRisse}
        gran={gran}
        zielTage={zielTage}
        zielWochen={zielWochen}
        importWochen={daten.importWochen ?? []}
      />

      {/* 6. Prognose — Zukunft, deshalb immer auf Wochen-Ebene */}
      <Karte
        titel="Was kommt in den nächsten Wochen wirklich an?"
        frage="Heller Balken: so viele Aufträge hat der Lieferant für die Woche angekündigt. Dunkle Linie: so viele kommen nach bisheriger Erfahrung wirklich. (Zukunft — immer je KW.)"
        hinweis="Die Erwartung ist bewusst vorsichtig gerechnet: Aufträge, die aus früheren Wochen in diese Woche nachrutschen, sind nicht eingerechnet. Wie die Zahl zustande kommt, steht beim Zeigen auf die Woche."
        tabelle={<PrognoseTabelle zeilen={prognoseZeilen} />}
      >
        {prognoseZeilen.length === 0 ? <Leer /> : (
          <>
            <Legende eintraege={PROGNOSE_LEGENDE} />
            <PrognoseDiagramm zeilen={prognoseZeilen} />
            <FazitBox text={fazitPrognose(prognoseZeilen)} />
          </>
        )}
      </Karte>

      {/* 7. Verschiebungen */}
      <Karte
        titel="Wie oft plant der Lieferant um?"
        frage="Rote Balken nach oben: Termine nach hinten verschoben. Grüne nach unten: vorgezogen. Keine Balken = ruhig."
        tabelle={<VerschiebungTabelle zeilen={verschiebungZeilen} gran={gran} />}
      >
        {verschiebungZeilen.length === 0 ? <Leer /> : (
          <>
            <Legende eintraege={VERSCHIEBUNG_LEGENDE} />
            <VerschiebungenDiagramm zeilen={verschiebungZeilen} achse={verschiebungAchse(verschiebungZeilen)} gran={gran} />
            <FazitBox text={fazitVerschiebungen(verschiebungZeilen)} />
          </>
        )}
      </Karte>

      {/* 8. Wartezeit */}
      <Karte
        titel="Wird die Wartezeit länger?"
        frage="Wie viele Tage lagen zwischen Auftragsbestätigung und Versand? Je Balken der typische Wert (Median) der ausgelieferten Aufträge."
        hinweis="Vereinzelt datiert eine Auftragsbestätigung NACH der Lieferung — das ist ein Datenfehler des Lieferanten. Solche Fälle fließen nicht in die Balken ein und stehen oben als eigene Kachel."
        tabelle={<VorlaufTabelle zeilen={vorlaufZeilen} gran={gran} />}
      >
        {vorlaufZeilen.length === 0 ? <Leer /> : (
          <>
            <VorlaufDiagramm zeilen={vorlaufZeilen} gran={gran} />
            <FazitBox text={fazitVorlauf(vorlaufZeilen)} />
          </>
        )}
      </Karte>

      {/* 9. Auffaellige Auftraege */}
      <AuffaelligeKarte auffaellig={daten.auffaellig} />

      {/* 10. Artikel und Marke — gesamte Historie, Marken < 10 Faelle ausgeblendet.
              Extern entfaellt die Marken-Karte (s. Prop `variante`). */}
      <div style={{ display: 'grid', gap: '1.25rem', gridTemplateColumns: 'repeat(auto-fit, minmax(340px, 1fr))' }}>
        <GruppenKarte
          titel="Welche Artikel verzögern sich?"
          frage="Wie oft kam ein Artikel später als vom Lieferanten zuerst geplant? Die 20 auffälligsten Artikel (gesamte Historie)."
          gruppen={daten.artikel}
          spalte="Artikel"
          einheit="Artikel"
        />
        {variante === 'intern' && (
          <GruppenKarte
            titel="Welche Marke verzögert sich öfter?"
            frage="Wie oft kamen Aufträge einer Marke später als zuerst geplant? (gesamte Historie)"
            gruppen={daten.hersteller}
            spalte="Marke (Plenty-Stammdaten)"
            einheit="Hersteller"
            minFaelle={10}
            zusatzHinweis={
              'Das ist das Feld "Hersteller" aus den Plenty-Stammdaten — eine Marke, KEINE '
              + 'Aufteilung nach Lieferant. Die ganze Liste kommt von einem Lieferanten; '
              + 'ein fremder Markenname bedeutet nicht, dass ein anderer Lieferant beteiligt war.'
            }
          />
        )}
      </div>
    </div>
  );
}

// ─── Steuerleiste ────────────────────────────────────────────────────────────

function Steuerleiste({
  gran, aufGran, zeitraum, aufZeitraum, perioden,
}: {
  gran: Granularitaet;
  aufGran: (g: Granularitaet) => void;
  zeitraum: Zeitraum;
  aufZeitraum: (z: Zeitraum) => void;
  perioden: string[];
}) {
  const granOptionen: Array<{ key: Granularitaet; label: string }> = [
    { key: 'kw', label: 'KW' }, { key: 'monat', label: 'Monat' }, { key: 'jahr', label: 'Jahr' },
  ];
  const selectStil: CSSProperties = {
    padding: '0.3rem 0.5rem', fontSize: '0.8125rem', border: `1px solid ${FARBEN.linie}`,
    borderRadius: '6px', background: FARBEN.flaeche, color: FARBEN.tinteStark,
  };
  return (
    <div style={{
      display: 'flex', alignItems: 'center', gap: '1rem', flexWrap: 'wrap',
      padding: '0.6rem 0.85rem', marginBottom: '1.25rem',
      background: FARBEN.flaeche, border: `1px solid ${FARBEN.linie}`, borderRadius: '10px',
    }}>
      <span style={{ fontSize: '0.8125rem', fontWeight: 600, color: FARBEN.tinteStark }}>Ansicht</span>
      <div style={{ display: 'flex', border: `1px solid ${FARBEN.linie}`, borderRadius: '6px', overflow: 'hidden' }}>
        {granOptionen.map((o) => {
          const an = o.key === gran;
          return (
            <button
              key={o.key}
              onClick={() => aufGran(o.key)}
              style={{
                padding: '0.3rem 0.8rem', fontSize: '0.8125rem', cursor: 'pointer', border: 'none',
                background: an ? FARBEN.tinteStark : FARBEN.flaeche,
                color: an ? '#FFFFFF' : FARBEN.tinteMittel, fontWeight: an ? 600 : 400,
              }}
            >
              {o.label}
            </button>
          );
        })}
      </div>

      <span style={{ fontSize: '0.8125rem', fontWeight: 600, color: FARBEN.tinteStark }}>Zeitraum</span>
      <label style={{ display: 'inline-flex', alignItems: 'center', gap: '0.35rem', fontSize: '0.8125rem', color: FARBEN.tinteMittel }}>
        von
        <select
          value={zeitraum.von ?? ''}
          onChange={(e) => aufZeitraum({ ...zeitraum, von: e.target.value || null })}
          style={selectStil}
        >
          <option value="">erster Import</option>
          {perioden.map((p) => <option key={p} value={p}>{periodeLabel(p, gran)}</option>)}
        </select>
      </label>
      <label style={{ display: 'inline-flex', alignItems: 'center', gap: '0.35rem', fontSize: '0.8125rem', color: FARBEN.tinteMittel }}>
        bis
        <select
          value={zeitraum.bis ?? ''}
          onChange={(e) => aufZeitraum({ ...zeitraum, bis: e.target.value || null })}
          style={selectStil}
        >
          <option value="">heute</option>
          {perioden.map((p) => <option key={p} value={p}>{periodeLabel(p, gran)}</option>)}
        </select>
      </label>
      {(zeitraum.von !== null || zeitraum.bis !== null) && (
        <button onClick={() => aufZeitraum(GANZER_ZEITRAUM)} style={knopfStil}>Zurücksetzen</button>
      )}
      <span style={{ fontSize: '0.75rem', color: FARBEN.tinteLeise }}>
        gilt für alle Verlaufs-Diagramme (die Prognose blickt immer nach vorn)
      </span>
    </div>
  );
}

// ─── 6-Wochen-Ziel-Karte ─────────────────────────────────────────────────────

function ZielKarte({
  zeilen, risse, offene, gran, zielTage, zielWochen, importWochen,
}: {
  zeilen: ReturnType<typeof baueZielZeilen>;
  risse: ReturnType<typeof zielRisse>;
  offene: ReturnType<typeof offeneUeberZiel>;
  gran: Granularitaet;
  zielTage: number;
  zielWochen: number;
  importWochen: string[];
}) {
  const [liste, setListe] = useState<'geliefert' | 'offen'>('geliefert');
  const aktiveListe = liste === 'geliefert' ? risse : offene;
  const zielLegende: LegendenEintrag[] = [
    { label: `innerhalb ${zielWochen} Wochen geliefert`, farbe: FARBEN.gut },
    { label: 'Ziel gerissen', farbe: FARBEN.kritisch },
  ];

  return (
    <Karte
      titel={`Wer reißt das ${zielWochen}-Wochen-Ziel?`}
      frage={`Von der Auftragsbestätigung (ohne AB: von der Bestellung) bis zum Versand sollen höchstens ${zahl(zielTage)} Tage vergehen — dieselbe Grenze wie die Warn-Spalte im Import. Grün schaffte es, Rot nicht.`}
      hinweis="Die Uhr startet mit dem AB-Datum; fehlt die AB, zählt ersatzweise das Bestelldatum aus Plenty (in der Liste gekennzeichnet). Nur Aufträge ganz ohne Datum bleiben draußen — sie stehen im Tooltip der Periode."
      tabelle={<ZielTabelle zeilen={zeilen} gran={gran} />}
    >
      {zeilen.length === 0 && offene.length === 0 ? <Leer /> : (
        <>
          <Legende eintraege={zielLegende} />
          {zeilen.length > 0 && <ZielDiagramm zeilen={zeilen} gran={gran} zielTage={zielTage} importWochen={importWochen} />}
          <FazitBox text={fazitZiel(zeilen, offene, zielTage)} />

          {/* Die konkreten Auftraege — das will man im Zweifel nachschlagen. */}
          {(risse.length > 0 || offene.length > 0) && (
            <div style={{ marginTop: '1rem' }}>
              <div style={{ display: 'flex', gap: '0.4rem', flexWrap: 'wrap', marginBottom: '0.65rem' }}>
                {([
                  { key: 'geliefert' as const, label: `geliefert, Ziel gerissen (${risse.length})` },
                  { key: 'offen' as const, label: `offen, schon über dem Ziel (${offene.length})` },
                ]).map((o) => {
                  const an = o.key === liste;
                  return (
                    <button
                      key={o.key}
                      onClick={() => setListe(o.key)}
                      style={{
                        padding: '0.3rem 0.7rem', fontSize: '0.8125rem', cursor: 'pointer',
                        borderRadius: '999px', border: `1px solid ${an ? FARBEN.tinteStark : FARBEN.linie}`,
                        background: an ? FARBEN.tinteStark : FARBEN.flaeche,
                        color: an ? '#FFFFFF' : FARBEN.tinteMittel, fontWeight: an ? 600 : 400,
                      }}
                    >
                      {o.label}
                    </button>
                  );
                })}
              </div>
              {aktiveListe.length === 0 ? (
                <p style={{ margin: 0, fontSize: '0.8125rem', color: FARBEN.tinteLeise }}>
                  Kein Auftrag in dieser Gruppe.
                </p>
              ) : (
                // Klasse lv-liste: am Handy stehen die Spalten untereinander
                // (ein Steckbrief je Auftrag) — 340 px zeigten davon nur
                // eineinhalb, dort gilt 60vh (s. SCHMAL_CSS in bausteine.tsx).
                <div className="lv-liste" style={{ maxHeight: '340px', overflowY: 'auto', border: `1px solid ${FARBEN.raster}`, borderRadius: '8px', padding: '0 0.5rem' }}>
                <Tabelle kopf={[
                  'Auftrag', { label: 'Uhr läuft seit', links: true },
                  { label: liste === 'geliefert' ? 'Warenausgang' : 'Status', links: true },
                  { label: 'Wartezeit' }, { label: 'Artikel', links: true },
                ]}>
                  {aktiveListe.map((r) => (
                    <tr key={r.orderId}>
                      <Zelle monospace>{r.orderId}</Zelle>
                      <Zelle>
                        {datumKurz(r.abDatum)}
                        <span style={{ color: FARBEN.tinteLeise, marginLeft: '0.3rem' }}>
                          ({r.basis === 'ab' ? 'AB' : 'Bestellung — keine AB'})
                        </span>
                      </Zelle>
                      <Zelle>{r.offen ? 'wartet noch' : (r.lieferWoche ? kwLang(r.lieferWoche) : '—')}</Zelle>
                      <Zelle rechts>
                        <span style={{ color: FARBEN.kritisch, fontWeight: 600, whiteSpace: 'nowrap' }}>{tageMitWochen(r.tage)}</span>
                      </Zelle>
                      <Zelle leise>{r.artikel.length ? r.artikel.join(', ') : '—'}</Zelle>
                    </tr>
                  ))}
                </Tabelle>
                </div>
              )}
            </div>
          )}
        </>
      )}
    </Karte>
  );
}

// ─── Tabellen-Zwillinge der Diagramme ────────────────────────────────────────

function TreueTabelle({ zeilen, gran }: { zeilen: TreueZeile[]; gran: Granularitaet }) {
  return (
    <Tabelle kopf={['Warenausgang in', 'pünktlich', '1 Wo. später', '2–3 Wo.', '4+ Wo.', 'gesamt', 'letzte Meldung gehalten', 'erste Meldung gehalten']}>
      {zeilen.filter((z) => z.gesamt > 0 || z.ersteMessbar > 0).map((z) => (
        <tr key={z.periode}>
          <Zelle>{periodeLang(z.periode, gran)}</Zelle>
          <Zelle rechts>{z.puenktlich}</Zelle>
          <Zelle rechts>{z.spaet1}</Zelle>
          <Zelle rechts>{z.spaet2bis3}</Zelle>
          <Zelle rechts>{z.spaet4plus}</Zelle>
          <Zelle rechts>{z.gesamt}</Zelle>
          <Zelle rechts>
            {z.quoteLetzte === null ? '—' : <>{z.puenktlich} von {z.gesamt} <span style={{ color: FARBEN.tinteLeise }}>({prozent(z.quoteLetzte)})</span></>}
          </Zelle>
          <Zelle rechts leise>
            {z.quoteErste === null ? '—' : `${z.ersteGehalten} von ${z.ersteMessbar} (${prozent(z.quoteErste)})`}
          </Zelle>
        </tr>
      ))}
    </Tabelle>
  );
}

function ZielTabelle({ zeilen, gran }: { zeilen: ReturnType<typeof baueZielZeilen>; gran: Granularitaet }) {
  return (
    <Tabelle kopf={['Warenausgang in', 'im Ziel', 'gerissen', 'Anteil gerissen', 'ohne AB']}>
      {zeilen.filter((z) => z.gesamt > 0 || z.ohneAb > 0).map((z) => (
        <tr key={z.periode}>
          <Zelle>{periodeLang(z.periode, gran)}</Zelle>
          <Zelle rechts>{z.imZiel}</Zelle>
          <Zelle rechts>{z.gerissen}</Zelle>
          <Zelle rechts>{z.quoteGerissen === null ? '—' : prozent(z.quoteGerissen)}</Zelle>
          <Zelle rechts leise>{z.ohneAb}</Zelle>
        </tr>
      ))}
    </Tabelle>
  );
}

function PrognoseTabelle({ zeilen }: { zeilen: ReturnType<typeof bauePrognoseZeilen> }) {
  return (
    <Tabelle kopf={['Zielwoche', 'angekündigt', 'zu erwarten', 'wahrscheinlich zwischen', 'Vergleichsfälle']}>
      {zeilen.map((p) => (
        <tr key={p.woche}>
          <Zelle>{kwLang(p.woche)}</Zelle>
          <Zelle rechts>{p.plan}</Zelle>
          <Zelle rechts>{p.erwartet}</Zelle>
          <Zelle rechts leise>{p.bandUnten} und {p.bandOben}</Zelle>
          <Zelle rechts leise>{p.basis}{p.duenn && <DuennMarke />}</Zelle>
        </tr>
      ))}
    </Tabelle>
  );
}

function VerschiebungTabelle({ zeilen, gran }: { zeilen: ReturnType<typeof baueVerschiebungZeilen>; gran: Granularitaet }) {
  return (
    <Tabelle kopf={['Umgeplant in', 'nach hinten', 'nach vorne']}>
      {zeilen.filter((v) => v.nachHinten > 0 || v.nachVorne > 0).map((v) => (
        <tr key={v.periode}>
          <Zelle>{periodeLang(v.periode, gran)}</Zelle>
          <Zelle rechts>{v.nachHinten}</Zelle>
          <Zelle rechts>{v.nachVorne}</Zelle>
        </tr>
      ))}
    </Tabelle>
  );
}

function VorlaufTabelle({ zeilen, gran }: { zeilen: ReturnType<typeof baueVorlaufZeilen>; gran: Granularitaet }) {
  return (
    <Tabelle kopf={['Warenausgang in', 'typische Wartezeit', 'ausgewertete Aufträge']}>
      {zeilen.filter((v) => v.medianTage !== null).map((v) => (
        <tr key={v.periode}>
          <Zelle>{periodeLang(v.periode, gran)}</Zelle>
          <Zelle rechts>{zahl(v.medianTage)} Tage</Zelle>
          <Zelle rechts leise>{v.n}</Zelle>
        </tr>
      ))}
    </Tabelle>
  );
}

// ─── Auffaellige Auftraege ───────────────────────────────────────────────────

const AUFFAELLIG_GRUPPEN = [
  { key: 'stillVerzoegert' as const, titel: 'still verzögert', erklaerung: 'Termin verstrichen, ohne dass eine neue KW gemeldet wurde.' },
  { key: 'verschwunden' as const, titel: 'verschwunden', erklaerung: 'Seit dem letzten Lauf nicht mehr in der Liste — und kein Warenausgang gebucht.' },
  { key: 'mehrfachVerschoben' as const, titel: 'mehrfach verschoben', erklaerung: 'Noch offen und mindestens dreimal umgeplant.' },
  { key: 'ohneTermin' as const, titel: 'ohne Termin', erklaerung: 'In der Liste, aber ohne verwertbare Liefer-KW.' },
];

function AuffaelligeKarte({ auffaellig }: { auffaellig: AnalyticsErgebnis['auffaellig'] }) {
  const [gruppe, setGruppe] = useState<typeof AUFFAELLIG_GRUPPEN[number]['key']>('stillVerzoegert');
  const aktiv = AUFFAELLIG_GRUPPEN.find((g) => g.key === gruppe)!;
  const liste: AuffaelligerAuftrag[] = auffaellig[gruppe] ?? [];

  return (
    <Karte
      titel="Wo muss jemand hinschauen?"
      frage={aktiv.erklaerung}
    >
      <div style={{ display: 'flex', gap: '0.4rem', flexWrap: 'wrap', marginBottom: '0.85rem' }}>
        {AUFFAELLIG_GRUPPEN.map((g) => {
          const anzahl = (auffaellig[g.key] ?? []).length;
          const anAn = g.key === gruppe;
          return (
            <button
              key={g.key}
              onClick={() => setGruppe(g.key)}
              style={{
                padding: '0.3rem 0.7rem', fontSize: '0.8125rem', cursor: 'pointer',
                borderRadius: '999px', border: `1px solid ${anAn ? FARBEN.tinteStark : FARBEN.linie}`,
                background: anAn ? FARBEN.tinteStark : FARBEN.flaeche,
                color: anAn ? '#FFFFFF' : FARBEN.tinteMittel, fontWeight: anAn ? 600 : 400,
              }}
            >
              {g.titel} <span style={{ fontVariantNumeric: 'tabular-nums' }}>({anzahl})</span>
            </button>
          );
        })}
      </div>

      {liste.length === 0 ? (
        <p style={{ margin: 0, fontSize: '0.8125rem', color: FARBEN.tinteLeise }}>
          Kein Auftrag in dieser Gruppe — nichts zu tun.
        </p>
      ) : (
        <Tabelle kopf={[
          'Auftrag', { label: 'geplante KW', links: true }, 'Umplanungen', 'zuletzt gesehen',
          { label: 'Artikel', links: true },
        ]}>
          {liste.map((a) => (
            <tr key={a.orderId}>
              <Zelle monospace>{a.orderId}</Zelle>
              <Zelle>{a.geplanteWoche ?? '—'}</Zelle>
              <Zelle rechts>{a.verschiebungen}</Zelle>
              <Zelle rechts leise>{datumKurz(a.zuletztGesehen)}</Zelle>
              <Zelle leise>{a.artikel.length ? a.artikel.join(', ') : '—'}</Zelle>
            </tr>
          ))}
        </Tabelle>
      )}
    </Karte>
  );
}

// ─── Artikel / Hersteller ────────────────────────────────────────────────────

function GruppenKarte({
  titel, frage, gruppen, spalte, einheit, minFaelle = 0, zusatzHinweis,
}: {
  titel: string; frage: string; gruppen: GruppenQuote[]; spalte: string;
  einheit: 'Hersteller' | 'Artikel'; minFaelle?: number; zusatzHinweis?: string;
}) {
  const geteilt = teileGruppen(gruppen, minFaelle);
  const zeilen = sortiereGruppen(geteilt.sichtbar);
  const fussnote = geteilt.ausgeblendet > 0
    ? `${geteilt.ausgeblendet === 1 ? `1 ${einheit} mit` : `${geteilt.ausgeblendet} ${einheit} mit je`} weniger als ${minFaelle} abgeschlossenen Aufträgen ausgeblendet — so wenige Fälle ergeben keine belastbare Aussage.`
    : undefined;
  return (
    <Karte
      titel={titel}
      frage={frage}
      hinweis={['Gemessen gegen die ERSTE Terminmeldung des Lieferanten.', zusatzHinweis, fussnote].filter(Boolean).join(' ')}
    >
      {zeilen.length === 0 ? <Leer /> : (
        <>
          <Tabelle kopf={[spalte, 'Aufträge', 'davon später', { label: 'Anteil später' }]}>
            {zeilen.map((g) => (
              <tr key={g.schluessel}>
                <Zelle>
                  {g.bezeichnung}
                  {g.quote.duenn && <DuennMarke />}
                </Zelle>
                <Zelle rechts>{g.auftraege}</Zelle>
                <Zelle rechts>{g.verspaetet}</Zelle>
                <QuoteZelle quote={g.quote} />
              </tr>
            ))}
          </Tabelle>
          <FazitBox text={fazitGruppen(zeilen, einheit)} />
        </>
      )}
    </Karte>
  );
}

// ─── Kleinkram ───────────────────────────────────────────────────────────────

const knopfStil: CSSProperties = {
  display: 'inline-flex', alignItems: 'center', gap: '0.35rem',
  padding: '0.35rem 0.7rem', fontSize: '0.8125rem', cursor: 'pointer',
  border: `1px solid ${FARBEN.linie}`, borderRadius: '6px',
  background: FARBEN.flaeche, color: FARBEN.tinteMittel,
};

function Platzhalter({ text }: { text: string }) {
  return (
    <div style={{ padding: '3rem', textAlign: 'center', color: FARBEN.tinteLeise, fontSize: '0.875rem' }}>
      {text}
    </div>
  );
}

function Leer() {
  return (
    <p style={{ margin: 0, fontSize: '0.8125rem', color: FARBEN.tinteLeise }}>
      Für diese Auswertung liegen noch keine Daten vor.
    </p>
  );
}
